import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./UserPanel/Sidebar";

export default function UserPanel() {
  const navigate = useNavigate();
  const intervalRef = useRef(null);

  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [connectionLoading, setConnectionLoading] = useState(true);
  const [connectionOnline, setConnectionOnline] = useState(false);
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState("");

  const clearSession = useCallback(() => {
    localStorage.removeItem("user_access_token");
    localStorage.removeItem("user_refresh_token");
    localStorage.removeItem("user_data");
  }, []);

  const loadOrganization = useCallback(async (accessToken) => {
    const response = await fetch("/tenants/api/logo/", {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
      },
      cache: "no-store",
    });

    let data = {};

    try {
      data = await response.json();
    } catch {
      data = {};
    }

    if (!response.ok || data.ok !== true || !data.tenant) {
      throw new Error(
        data.message || "No fue posible cargar la organización."
      );
    }

    setOrganization(data.tenant);
  }, []);

  const loadConnectionStatus = useCallback(
    async (accessToken, showLoading = false) => {
      if (showLoading) {
        setConnectionLoading(true);
      }

      try {
        const response = await fetch(
          `/access-control/api/connector/status/?t=${Date.now()}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              Accept: "application/json",
              "Cache-Control": "no-cache",
            },
            cache: "no-store",
          }
        );

        let data = {};

        try {
          data = await response.json();
        } catch {
          data = {};
        }

        if (!response.ok || data.ok !== true) {
          setConnectionOnline(false);
          return;
        }

        setConnectionOnline(data.connection?.is_online === true);
      } catch {
        setConnectionOnline(false);
      } finally {
        if (showLoading) {
          setConnectionLoading(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    const initialize = async () => {
      const accessToken = localStorage.getItem("user_access_token");
      const storedUser = localStorage.getItem("user_data");

      if (!accessToken || !storedUser) {
        navigate("/login", { replace: true });
        return;
      }

      try {
        const parsedUser = JSON.parse(storedUser);

        if (parsedUser.user_type !== "ADMIN") {
          throw new Error("No tienes acceso.");
        }

        setUser(parsedUser);

        await Promise.all([
          loadOrganization(accessToken),
          loadConnectionStatus(accessToken, true),
        ]);

        intervalRef.current = window.setInterval(() => {
          loadConnectionStatus(accessToken, false);
        }, 60000);
      } catch (error) {
        clearSession();

        setMessage(
          error instanceof Error
            ? error.message
            : "No fue posible cargar la sesión."
        );

        window.setTimeout(() => {
          navigate("/login", { replace: true });
        }, 1000);
      } finally {
        setLoading(false);
      }
    };

    initialize();

    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [
    clearSession,
    loadConnectionStatus,
    loadOrganization,
    navigate,
  ]);

  const handleCopyToken = async () => {
    const token = organization?.access_token;

    if (!token) {
      return;
    }

    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
    } catch {
      setMessage("No fue posible copiar el token.");
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user || !organization) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        backgroundColor: "#f5f5f5",
      }}
    >
      <Sidebar
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        organization={organization}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          padding: {
            xs: 2,
            sm: 3,
            md: 4,
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 1100,
            margin: "0 auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              marginBottom: 4,
            }}
          >
            <IconButton
              onClick={() => setMobileOpen(true)}
              sx={{
                display: {
                  xs: "inline-flex",
                  md: "none",
                },
              }}
            >
              <MenuRoundedIcon />
            </IconButton>

            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1.2,
                }}
              >
                <Typography
                  component="h1"
                  sx={{
                    fontSize: {
                      xs: 25,
                      md: 31,
                    },
                    fontWeight: 800,
                    lineHeight: 1.2,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {organization.name}
                </Typography>

                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.7,
                    paddingX: 1.1,
                    paddingY: 0.45,
                    borderRadius: 2,
                    backgroundColor: connectionOnline
                      ? "#f0fdf4"
                      : "#fef2f2",
                    border: connectionOnline
                      ? "1px solid #bbf7d0"
                      : "1px solid #fecaca",
                  }}
                >
                  {connectionLoading ? (
                    <CircularProgress size={11} />
                  ) : (
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        backgroundColor: connectionOnline
                          ? "#16a34a"
                          : "#dc2626",
                      }}
                    />
                  )}

                  <Typography
                    sx={{
                      color: connectionOnline
                        ? "#166534"
                        : "#b91c1c",
                      fontSize: 11,
                      fontWeight: 800,
                      lineHeight: 1.2,
                    }}
                  >
                    {connectionLoading
                      ? "Verificando"
                      : connectionOnline
                        ? "En línea"
                        : "Fallo de conexión"}
                  </Typography>
                </Box>
              </Box>

              <Typography
                sx={{
                  marginTop: 0.5,
                  color: "#6b7280",
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                Bienvenido, {user.name}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              width: "100%",
              padding: {
                xs: 2.5,
                sm: 4,
              },
              backgroundColor: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: 3,
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 800,
                marginBottom: 1,
              }}
            >
              Token de acceso
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                padding: 1.5,
                borderRadius: 2,
                backgroundColor: "#f9fafb",
                border: "1px solid #e5e7eb",
              }}
            >
              <Typography
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  fontSize: 13,
                  fontWeight: 700,
                  wordBreak: "break-all",
                }}
              >
                {organization.access_token || "Token no disponible"}
              </Typography>

              <Tooltip title={copied ? "Copiado" : "Copiar token"}>
                <span>
                  <IconButton
                    onClick={handleCopyToken}
                    disabled={!organization.access_token}
                    sx={{
                      flexShrink: 0,
                      width: 38,
                      height: 38,
                      backgroundColor: copied ? "#166534" : "#000000",
                      color: "#ffffff",
                      "&:hover": {
                        backgroundColor: copied ? "#14532d" : "#272727",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#d1d5db",
                        color: "#ffffff",
                      },
                    }}
                  >
                    <ContentCopyRoundedIcon sx={{ fontSize: 19 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={copied}
        autoHideDuration={1800}
        onClose={() => setCopied(false)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity="success"
          onClose={() => setCopied(false)}
          sx={{ width: "100%" }}
        >
          Token copiado.
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={3000}
        onClose={() => setMessage("")}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity="error"
          onClose={() => setMessage("")}
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
}